import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { StatsCard } from './stats/StatsCard'
import inboxIcon from '@/assets/dashboard/inbox.svg'
import { formatCompactNumber } from '@/utils'

interface TotalOrdersCardProps {
  totalOrders: number
  loading?: boolean
}

interface TotalOrdersBarData {
  value: number
}

// Deterministic PRNG so bars depend on real values but don't "jump" on rerender.
function mulberry32(seed: number) {
  let t = seed >>> 0
  return () => {
    t += 0x6d2b79f5
    let r = Math.imul(t ^ (t >>> 15), 1 | t)
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

const TotalOrdersMiniBarChart: React.FC<{
  data: TotalOrdersBarData[]
  className?: string
  color?: string
  barWidthRatio?: number
}> = ({ data, className = '', barWidthRatio = 0.9 }) => {
  const normalizedData = useMemo(() => {
    const maxValue = Math.max(...data.map((d) => d.value), 1)
    return data.map((item) => ({
      ...item,
      height: (item.value / maxValue) * 50, // keep same height logic as current SimpleBarChart
    }))
  }, [data])

  const barWidth = 214 / normalizedData.length
  const grey = {
    borderTop: '#cccdc5',
    from: 'rgba(197,204,159,0.32)',
    to: 'rgba(255,255,255,0)',
  }
  const lime = {
    borderTop: '#c8ed03',
    from: 'rgba(200,237,3,0.3)',
    to: 'rgba(200,237,3,0)',
  }

  return (
    <div className={`relative w-full max-w-[214px] h-[80px] ${className}`}>
      {normalizedData.map((item, index) => {
        const leftPosition = index * barWidth
        const barPixelWidth = barWidth * barWidthRatio
        const centeredLeft = leftPosition + (barWidth - barPixelWidth) / 2
        // Figma 37:1124 has 8 grey bars followed by 6 lime bars.
        const palette = index >= 8 ? lime : grey

        return (
          <div
            key={`bar-${index}`}
            className="absolute bottom-0"
            style={{
              left: `${centeredLeft}px`,
              width: `${barPixelWidth}px`,
            }}
          >
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: item.height, opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: index * 0.05,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="w-full rounded-t-sm"
              style={{
                background: `linear-gradient(to bottom, ${palette.from} 0%, ${palette.to} 100%)`,
                borderTop: `2px solid ${palette.borderTop}`,
                height: `${item.height}px`,
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

/**
 * Screen: OTCScreen
 * Total Orders card extracted as a standalone component.
 * Must render exactly the same UI as the inline block in OTCMetricsCards.
 */
export const TotalOrdersCard: React.FC<TotalOrdersCardProps> = ({ totalOrders, loading = false }) => {
  const chartData = useMemo<TotalOrdersBarData[]>(() => {
    // 14 bars: 8 grey + 6 lime (Figma 37:1124 structure)
    const rng = mulberry32(Math.max(1, totalOrders))
    return Array.from({ length: 14 }, () => ({
      value: 10 + Math.round(rng() * 90), // 10..100
    }))
  }, [totalOrders])

  return (
    <StatsCard
      icon={inboxIcon}
      iconAlt="Total Orders"
      label="Total Orders"
      value={formatCompactNumber(totalOrders)}
      loading={loading}
      chart={
        <TotalOrdersMiniBarChart data={chartData} />
      }
    />
  )
}

